import { useEffect, useRef, useState } from 'react'
import styles from './SubcriptionItem.module.scss'
import { CalculateSubscriptionResult } from "api/query/Subscription"

type ISubcriptionItem = {
	title: string,
	subtitle: string,
	price: number,
	elClass: string,
	isSelected: boolean,
	changed: any,
	quantity?: number,
	onQuantityMinus?: () => void,
	onQuantityPlus?: () => void,
	mainGrz?: string | null,
	extraGrzList?: string[],
	extraGrzEnabled?: boolean,
	extraGrzMaxCount?: number,
	excludingCount?: number,
	onExtraGrzClick?: () => void,
	onGrzRemove?: (grz: string) => void,
	promoInput?: string,
	appliedPromoCode?: string,
	onPromoInputChange?: (value: string) => void,
	onPromoApply?: () => void,
	onPromoCancel?: () => void,
	timestartValue?: string,
	timeendValue?: string,
	showDateControl?: boolean,
	onTimestartChange?: (value: string) => void,
	calculation?: CalculateSubscriptionResult | null,
	calculationLoading?: boolean,
	calculationError?: string,
	isPromoAppliedSuccessfully?: boolean,
	showExtendedControls?: boolean,
}

const SubcriptionItem = ({
	title,
	subtitle,
	price,
	elClass,
	isSelected,
	changed,
	quantity = 1,
	onQuantityMinus,
	onQuantityPlus,
	mainGrz,
	extraGrzList = [],
	extraGrzEnabled = false,
	extraGrzMaxCount,
	excludingCount,
	onExtraGrzClick,
	onGrzRemove,
	promoInput = '',
	appliedPromoCode = '',
	onPromoInputChange,
	onPromoApply,
	onPromoCancel,
	timestartValue = '',
	timeendValue = '',
	showDateControl = false,
	onTimestartChange,
	calculation,
	calculationLoading = false,
	calculationError = '',
	isPromoAppliedSuccessfully = false,
	showExtendedControls = false,
}: ISubcriptionItem) => {
	const [extraGrzHintVisible, setExtraGrzHintVisible] = useState<boolean>(false);
	const extraGrzHintRef = useRef<HTMLDivElement | null>(null);
	const formatRub = (value: number | undefined) => {
		if (typeof value !== 'number') return '-';
		return Number.isInteger(value / 100) ? String(value / 100) : (value / 100).toFixed(2).replace('.', ',');
	};
	const showDiscount = !!calculation && calculation.discountAmount > 0;
	const promoErrorMessage = calculationError
		|| (appliedPromoCode && calculation?.promoCodeInf?.code !== 200 ? calculation?.promoCodeInf?.msg : '');
	const hasPromoError = !!promoErrorMessage;
	const allVisibleGrz = [mainGrz, ...extraGrzList].filter(Boolean) as string[];
	const formatDateString = (value: string) => {
		const normalized = value.replace('T', ' ').substring(0, 16);
		const [datePart = '', timePart = ''] = normalized.split(' ');
		const [year, month, day] = datePart.split('-');
		return year && month && day ? `${day}.${month}.${year} ${timePart}`.trim() : normalized;
	};

	useEffect(() => {
		if (!extraGrzHintVisible) return;
		const handleDocumentClick = (event: MouseEvent | TouchEvent) => {
			if (extraGrzHintRef.current?.contains(event.target as Node)) return;
			setExtraGrzHintVisible(false);
		};
		document.addEventListener('mousedown', handleDocumentClick);
		document.addEventListener('touchstart', handleDocumentClick);
		return () => {
			document.removeEventListener('mousedown', handleDocumentClick);
			document.removeEventListener('touchstart', handleDocumentClick);
		};
	}, [extraGrzHintVisible]);

	return (
		<div className={`${elClass} ${styles.item}`} onClick={(event) => {
			setExtraGrzHintVisible(false);
			changed(event);
			}}>
			{isSelected && extraGrzEnabled ? (
				<div
					className={styles.grz_badge}
					ref={extraGrzHintRef}
					onClick={(event) => {
						event.stopPropagation();
						setExtraGrzHintVisible(false);
					}}
				>
					<div className={styles.grz_badge_row}>
						<button
							type="button"
							className={styles.grz_add_btn}
							onClick={() => {
								setExtraGrzHintVisible(false);
								onExtraGrzClick?.();
							}}
							aria-label="Добавить госномер"
						>
							<svg width="20" height="20" viewBox="0 -960 960 960" fill="none" aria-hidden="true">
								<path fill="currentColor" d="M240-200v40q0 17-11.5 28.5T200-120h-40q-17 0-28.5-11.5T120-160v-320l84-240q6-18 21.5-29t34.5-11h440q19 0 34.5 11t21.5 29l84 240v320q0 17-11.5 28.5T800-120h-40q-17 0-28.5-11.5T720-160v-40H240Zm-8-360h496l-42-120H274l-42 120Zm-32 80v200-200Zm100 160q25 0 42.5-17.5T360-380q0-25-17.5-42.5T300-440q-25 0-42.5 17.5T240-380q0 25 17.5 42.5T300-320Zm360 0q25 0 42.5-17.5T720-380q0-25-17.5-42.5T660-440q-25 0-42.5 17.5T600-380q0 25 17.5 42.5T660-320Zm-460 40h560v-200H200v200Z"/>
							</svg>
							<span>Добавить авто</span>
						</button>
						<button
							type="button"
							className={styles.grz_info_btn}
							onClick={(event) => {
								event.stopPropagation();
								setExtraGrzHintVisible((state) => !state);
							}}
							aria-label="Информация по количеству автомобилей"
						>
							i
						</button>
					</div>
					{extraGrzHintVisible ? (
						<div
							className={styles.grz_hint}
							onClick={() => setExtraGrzHintVisible(false)}
						>
							{extraGrzMaxCount ? <b>Добавлено {allVisibleGrz.length} из {extraGrzMaxCount}</b> : null}
							{excludingCount ? <div>Одновременно на парковке: до {excludingCount}</div> : null}
						</div>
					) : null}
				</div>
			) : null}
			{isSelected && !extraGrzEnabled && mainGrz ? (
				<div className={styles.grz_single_badge} onClick={(event) => event.stopPropagation()}>
					{mainGrz}
				</div>
			) : null}
			<label>
				<input type="radio" className={elClass} onChange={changed} checked={isSelected} />
				<span className={`${elClass} ${styles.radio} ${isSelected ? styles.radio__active : ''}`}/>
			</label>
			{isSelected && extraGrzEnabled && allVisibleGrz.length > 0 ? (
				<div className={styles.grz_card_list} onClick={(event) => event.stopPropagation()}>
					{allVisibleGrz.map((grz) => (
						<span key={grz}>
							{grz}
							{extraGrzEnabled && allVisibleGrz.length > 1 ? (
								<button
									type="button"
									onClick={() => {
										setExtraGrzHintVisible(false);
										onGrzRemove?.(grz);
									}}
									aria-label={`Удалить ${grz}`}
								>
									×
								</button>
							) : null}
						</span>
					))}
				</div>
			) : null}
			<div className={`${elClass} ${styles.item_title}`}>{title}</div>
			<div className={`${elClass} ${styles.item_subtitle}`} dangerouslySetInnerHTML={{ __html: subtitle }} />
			<div className={styles.item_bottom}>
				<div className={styles.price_block}>
					<div className={`${elClass} ${styles.item_price}`}>{`${price} руб.`}</div>
				</div>
				{isSelected && showExtendedControls ? (
					<div className={styles.quantity_control} onClick={(event) => event.stopPropagation()}>
						<button
							type="button"
							className={styles.quantity_btn}
							onClick={onQuantityMinus}
							disabled={quantity <= 1}
						>
							-
						</button>
						<div className={styles.quantity_value}>{quantity}</div>
						<button
							type="button"
							className={styles.quantity_btn}
							onClick={onQuantityPlus}
						>
							+
						</button>
					</div>
				) : null}
			</div>
			{isSelected && showExtendedControls ? (
				<div className={styles.selected_details} onClick={(event) => event.stopPropagation()}>
					{showDateControl ? (
						<div className={styles.period_block}>
							<div className={styles.period_item}>
								<div className={styles.period_title}>Выберите дату начала абонемента:</div>
								<input
									type="datetime-local"
									value={timestartValue}
									onChange={(event) => onTimestartChange?.(event.target.value)}
									className={styles.date_input}
								/>
							</div>
							<div className={styles.period_item}>
								<div className={styles.period_title}>Дата окончания абонемента:</div>
								<div className={styles.period_value}>{formatDateString(timeendValue)}</div>
							</div>
						</div>
					) : null}
					<div className={styles.promo_title}>Промокод:</div>
					<div className={styles.promo_row}>
						<div className={styles.promo_input_wrap}>
							<input
								type="text"
								value={promoInput}
								onChange={(event) => onPromoInputChange?.(event.target.value)}
								className={`${styles.promo_input} ${isPromoAppliedSuccessfully ? styles.promo_input_readonly : ''} ${hasPromoError ? styles.promo_input_error : ''} ${(isPromoAppliedSuccessfully && showDiscount) || hasPromoError ? styles.promo_input_with_status : ''}`}
								placeholder="Промокод"
								readOnly={isPromoAppliedSuccessfully}
							/>
							{isPromoAppliedSuccessfully && showDiscount ? (
								<span className={`${styles.promo_status_inside} ${styles.promo_status_success}`}>-{formatRub(calculation?.discountAmount)} руб.</span>
							) : hasPromoError ? (
								<span className={`${styles.promo_status_inside} ${styles.promo_status_error}`}>не применен</span>
							) : null}
						</div>
						{isPromoAppliedSuccessfully ? (
							<button type="button" className={`${styles.promo_btn} ${styles.promo_btn_secondary}`} onClick={onPromoCancel} disabled={calculationLoading}>
								Отменить
							</button>
						) : (
							<button
								type="button"
								className={styles.promo_btn}
								onClick={onPromoApply}
								disabled={calculationLoading || !promoInput.trim() || promoInput.trim() === appliedPromoCode}
							>
								Применить
							</button>
						)}
					</div>

				</div>
			) : null}
		</div>
	)
}

export default SubcriptionItem;
